import * as CookieConsent from 'vanilla-cookieconsent';
import posthog from 'posthog-js';
import { requiresConsentBanner } from './geo';
import { initFingerprint, clearFingerprint } from './collect';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export function initConsent(): Promise<void> {
  const showBanner = requiresConsentBanner();

  return CookieConsent.run({
    // Geo-gate: GDPR-region visitors (Europe/Atlantic/unknown timezones) get
    // the consent banner; everyone else tracks by default and gets the
    // dismissible notice below instead.
    autoShow: showBanner,

    guiOptions: {
      consentModal: {
        layout: 'bar inline',
        position: 'bottom',
        equalWeightButtons: false,
        flipButtons: true,
      },
    },

    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      analytics: {
        enabled: false,
        autoClear: {
          cookies: [
            { name: /^_ga/ },
            { name: '_gid' },
            { name: /^ph_/ },
          ],
        },
      },
    },

    language: {
      default: 'en',
      translations: {
        en: {
          consentModal: {
            title: '',
            description:
              'We enhance your experience using smart analytics. <a href="/privacy">Privacy Policy</a>',
            acceptAllBtn: 'Go enhanced',
            acceptNecessaryBtn: 'Stay basic',
          },
          preferencesModal: {
            title: 'Cookie preferences',
            acceptAllBtn: 'Accept all',
            // v3 names the reject button acceptNecessaryBtn; the old
            // rejectAllBtn key was silently ignored by the library.
            acceptNecessaryBtn: 'Reject all',
            savePreferencesBtn: 'Save',
            closeIconLabel: 'Close',
            sections: [
              {
                title: 'Necessary cookies',
                description: 'Required for the site to function.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Analytics cookies',
                description:
                  'Help us understand how visitors interact with the site.',
                linkedCategory: 'analytics',
              },
            ],
          },
        },
      },
    },

    onConsent() {
      applyConsent();
    },

    onChange() {
      applyConsent();
    },
  }).then(() => {
    if (showBanner) return;
    if (!CookieConsent.validConsent()) {
      // First visit from a non-banner region: opt in programmatically. This
      // persists cc_cookie, fires onConsent -> applyConsent, and activates the
      // data-category scripts in index.html. A later "Opt out" via the
      // notice/preferences is respected on every future visit by the
      // validConsent() check above.
      CookieConsent.acceptCategory('all');
    }
  });
}

function applyConsent() {
  if (CookieConsent.acceptedCategory('analytics')) {
    window.gtag?.('consent', 'update', {
      analytics_storage: 'granted',
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
    });
    posthog.opt_in_capturing();
    initFingerprint();
  } else {
    window.gtag?.('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    posthog.opt_out_capturing();
    clearFingerprint();
  }
}

